import styled from "@emotion/styled";
import React, { useMemo, useState } from "react";
import { tracker, useBloc } from "src/state/state";
import { GroupedCategory } from "../LabStatusOverview/LabStatusOverviewCubit";
import {
  BlueCheckmarkBadgeSmall,
  BlueCheckmarkSmall,
  ChevronDownIcon,
  GrayCrossSmall,
  GrayQuestionmark,
  RedCrossSmall
} from "src/constants/icons";
import Translate from "../Translate/Translate";
import UserCubit, {
  ComplexConditionGroup
} from "src/state/UserCubit/UserCubit";

const Container = styled.div`
  label: LabCategoryToggleContainer;
  border-bottom: 1px solid var(--color-gray-light);

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-sm);
  background-color: var(--color-white);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:active {
    background-color: var(--color-fog);
  }

  &:focus-visible {
    outline: 2px solid var(--color-sunrise-blue);
    outline-offset: -2px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CategoryTitle = styled.h3`
  label: LabCategoryTitle;
  font-size: 1.1rem;
  font-family: var(--font-family);
  font-style: normal;
  font-weight: 500;
  text-align: left;
`;

enum LabCategoryStatus {
  Completed = "completed",
  Incomplete = "incomplete",
  Partial = "partial"
}

const StatusBadge = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
`;

const ChevronIcon = styled.span`
  display: inline-block;
  transform: rotate(0deg);
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;

  &[aria-expanded="true"] {
    transform: rotate(180deg);
  }
`;

const Content = styled.div`
  padding: 0 var(--space-sm) var(--space-sm);
  display: none;

  &[aria-hidden="false"] {
    display: block;
  }
`;

const CategoryInfoText = styled.div`
  label: LabCategoryInfoText;
  font-size: 0.725rem;
  font-weight: 500;
  text-align: left;
  width: 100%;
  background-color: var(--color-fog);
  margin-top: var(--space-sm);
  border-radius: 0.2rem;
  padding: var(--space-xs);
`;

const ItemContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.3rem;
`;

const LabItem = styled.div`
  label: LabItem;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  svg {
    width: 16px;
    height: 16px;
    margin: 0;
    display: inline-block;
  }
`;

const ItemInfo = styled.div``;

const ItemName = styled.span`
  label: LabItemName;
  font-size: 0.875rem;
  font-weight: 400;
`;

interface LabCategoryToggleProps {
  category: GroupedCategory;
}

const LabCategoryToggle: React.FC<LabCategoryToggleProps> = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [, { labsConditionGroup }] = useBloc(UserCubit);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    // Track analytics
    if (newExpanded) {
      tracker.track("Lab Category Expanded", {
        data: {
          category: category.category,
          displayName: category.displayName,
          itemCount: category.items.length,
          hasRequired: category.hasRequired,
          completedLabs: category.items
            .filter((item) => item.status === "COMPLETE")
            .map((item) => item.displayName),
          requiredLabs: category.items
            .filter((item) => item.isRequired)
            .map((item) => item.displayName),
          incompleteLabs: category.items
            .filter((item) => item.status !== "COMPLETE")
            .map((item) => item.displayName)
        }
      });
    }
  };

  const status = useMemo<LabCategoryStatus>(() => {
    if (category.hasRequired) {
      return category.requiredCompleted
        ? LabCategoryStatus.Completed
        : LabCategoryStatus.Incomplete;
    }

    return category.allCompleted
      ? LabCategoryStatus.Completed
      : LabCategoryStatus.Incomplete;
  }, [category]);

  const labGroupMonths = useMemo(() => {
    switch (labsConditionGroup) {
      case ComplexConditionGroup.HIGH_RISK:
        return 3;
      case ComplexConditionGroup.MODERATE_RISK:
        return 6;
      case ComplexConditionGroup.LOW_RISK:
        return 12;
      default:
        return 3;
    }
  }, [labsConditionGroup]);

  return (
    <Container>
      <HeaderButton
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls={`category-${category.category}-content`}
      >
        <HeaderContent>
          <StatusBadge>
            {status === LabCategoryStatus.Completed && (
              <BlueCheckmarkBadgeSmall />
            )}
            {status !== LabCategoryStatus.Completed && <GrayQuestionmark />}
          </StatusBadge>
          <CategoryTitle>{category.displayName}</CategoryTitle>
        </HeaderContent>
        <ChevronIcon aria-expanded={isExpanded}>
          <ChevronDownIcon width={24} height={24} />
        </ChevronIcon>
      </HeaderButton>

      <Content
        aria-hidden={!isExpanded}
        id={`category-${category.category}-content`}
      >
        <ItemContainer>
          {category.items.map((item, index) => (
            <LabItem key={`${item.type}-${index}`}>
              {item.status === "COMPLETE" ? (
                <BlueCheckmarkSmall />
              ) : (
                <>{item.isRequired ? <RedCrossSmall /> : <GrayCrossSmall />}</>
              )}
              <ItemInfo>
                <ItemName>
                  {item.displayName}
                  {item.isRequired && " *"}
                </ItemName>
              </ItemInfo>
            </LabItem>
          ))}
        </ItemContainer>

        {category.hasRequired && (
          <CategoryInfoText>
            <Translate
              msg="labCategory.noticeRequired"
              variables={{ months: labGroupMonths }}
              markdown
              disabledTags={["p"]}
            />
          </CategoryInfoText>
        )}
      </Content>
    </Container>
  );
};

export default LabCategoryToggle;
